import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Grid, Row } from 'components/containers';
import { WrapperCard } from 'components/elements';
import {
  Autocomplete,
  DatePicker,
  Form,
  MonthYearPicker,
  Select,
  TextField,
  useForm,
  useFormFieldObserver,
} from 'components/form';
import { FormInstance } from 'components/form/types';
import { useTranslationX } from 'i18n';

import styles from './RHExpenseForm.module.scss';
import { useFetchWithEnable } from 'apis';
import { useExpenseDetailContext } from 'pages/finance/ConciliationExpenseDetail/ConciliationExpenseDetailContext';

type Props = {
  onDone: VoidFunction;
  formRef: React.RefObject<FormInstance>;
  id: number;
};


type SuggestionResponse = {
  id: number;
  cnpj: string;
  credor: string;
};

const SALARY_ID = 15;

const RHExpenseForm = ({ formRef, onDone, id }: Props) => {
  const { tx } = useTranslationX('expenses', 'finance');

  const { expense } = useExpenseDetailContext();

  const Content = () => {

    const form = useForm();

    const personDataRef = useRef<{
      id: number;
      nome: string;
      cpf: string;
    }>();

    const identificacaoObserver = useFormFieldObserver('identificacaoDespesaConciliacao');

    const identificacaoId = identificacaoObserver.value;

    const competenciaObserver = useFormFieldObserver('competencia');
    const competencia = competenciaObserver.value as
      | { year: number; month: number }
      | undefined;

    const [beneficiarioNome, setBeneficiarioNome] = useState<string>();

    const identificacaoNome = useMemo(() => {
      const options = form.getFieldMetadata('identificacaoDespesaConciliacao')?.options;
      if (Array.isArray(options)) {
        return options.find(option => option.value === identificacaoId)?.text;
      }
      return undefined;
    }, [form, identificacaoId]);

    const competenciaLabel = competencia
      ? `${String(competencia.month).padStart(2, '0')}/${competencia.year}`
      : '';

    useEffect(() => {
      const numeroNotaFiscal = [
        identificacaoNome ?? '',
        beneficiarioNome ?? '',
        competenciaLabel,
      ].join(' - ');
      form.setFieldValue('numeroNotaFiscal', numeroNotaFiscal);
    }, [form, identificacaoNome, beneficiarioNome, competenciaLabel]);


    const { data: sugestaoPreenchimento } = useFetchWithEnable<SuggestionResponse>(
      `/financeiro/conciliacao/despesas/sugestao-preenchimento?tipo=dr&identificacaoId=${identificacaoId}`,
      !!identificacaoId
    );

    const updateCreditorFields = useCallback(
      (values: { nome: string; cpf: string }) => {
        form.setFieldValue('credor', values.nome);
        form.setFieldValue('cnpjCpf', values.cpf);
      },
      [form]
    );

    useEffect(() => {
      if (identificacaoId === SALARY_ID && personDataRef.current) {
        updateCreditorFields(personDataRef.current);
      }
    }, [form, identificacaoId, updateCreditorFields]);

    const handleSelectOption = useCallback(
      (option: any) => {
        setBeneficiarioNome(option.pessoaNome ?? option.text);
        if (identificacaoId === SALARY_ID) {
          updateCreditorFields({
            nome: option.pessoaNome,
            cpf: option.pessoaCpf,
          });
        } else {
          personDataRef.current = {
            id: option.id,
            nome: option.pessoaNome,
            cpf: option.pessoaCpf,
          };
        }
      },
      [identificacaoId, updateCreditorFields]
    );

    return (
      <Grid spacing={0}>
        <Row>
          <Autocomplete
            url={`/financeiro/conciliacao/despesas/contas-projeto/${id}/beneficiarios`}
            name="projetoRh"
            placeholder={tx('searchBeneficiary')}
            fetchDetail={false}
            label={tx('beneficiary')}
            onSelectOption={handleSelectOption}
            formatOption={item => item.text}
            minCharacters={0}
          />
        </Row>
        <Row>
          <MonthYearPicker
            style={{
              root: {
                fontSize: 12,
              },
            }}
            label={tx('competence')}
            name="competencia"
            theme="classic"
          />
          <Select name="tipo" label={tx('type')} />
          <Select
            name="identificacaoDespesaConciliacao"
            label={tx('identificationField')}
          />
        </Row>

        <Row width={[0.5, 0.5]}>
          <TextField
            name="credor"
            label={tx('creditor')}
            autoSuggestionParams={{
              value: sugestaoPreenchimento?.credor,
            }}
            type={identificacaoId === SALARY_ID ? 'readonly' : undefined}
          />
        </Row>
        <Row width={[0.5, 0.5]}>
          <TextField
            name="cnpjCpf"
            label={tx('cnpjCpf')}
            type={identificacaoId === SALARY_ID ? 'readonly' : undefined}
            autoSuggestionParams={{
              value: sugestaoPreenchimento?.cnpj,
            }}
          />
        </Row>
        <div className={styles.container}>
          <h1>{tx('invoice')}</h1>
          <Row>
            <TextField
              name="numeroNotaFiscal"
              label={tx('invoiceNumber')}
              type="readonly"
            />
          </Row>
          <Row width={[0.5, 0.5]}>
            <DatePicker
              name="dataNotaFiscal"
              label={tx('invoiceDate')}
              autoSuggestionParams={{
                value: expense?.transactionDate,
              }}
            />
            <TextField
              name="valor"
              label={tx('value')}
              autoSuggestionParams={{
                value: expense?.availableValue,
              }}
            />
          </Row>
        </div>
      </Grid>
    );
  };

  return (
    <>
      <WrapperCard maxWidth="100%" padding="30px 32px">
        <Form
          baseUrl={`/financeiro/conciliacao/despesas/contas-projeto/${id}/despesa-rh`}
          ref={formRef}
          onDone={onDone}
          submit={{
            method: 'post',
          }}
        >
          <Content />
        </Form>
      </WrapperCard>
    </>
  );
};

export default RHExpenseForm;
